// Плеер сценария (К-06): FR-PLAY-01…10, 12; FR-RW-01…03; FR-BOARD-06…09.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Board, piecesFromScene, poseAt, usePlayback, type BoardHandle } from "@brovka/board";
import type { CompiledScenario, Step, StepTimeline } from "@brovka/content-types";
import { SpeakerIcon } from "@brovka/ui";
import { ComplaintSheet } from "../components/ComplaintSheet.tsx";
import { TopBar } from "../components/Shell.tsx";
import { audio } from "../lib/audio.ts";
import { findExercise, findScenario, phraseUrl, uiText, uiUrl } from "../lib/content/catalog.ts";
import { record } from "../lib/events.ts";
import { cellsOf, frameOf } from "../quiz/engine.ts";
import { activeChild, useApp } from "../state/app.ts";
import { t } from "../i18n/ru.ts";

type Level = "kid" | "parent" | "coach";
type Track = { steps: StepTimeline[]; texts: Step[]; wrong: boolean };

function track(s: CompiledScenario, branchId: string | null): Track {
  const main = s.timelines.main?.steps ?? [];
  if (!branchId) return { steps: main, texts: s.scenario.steps, wrong: false };
  const b = s.scenario.branches?.find((x) => x.id === branchId);
  const bt = s.timelines[branchId]?.steps ?? [];
  if (!b) return { steps: main, texts: s.scenario.steps, wrong: false };
  return { steps: [...main.slice(0, b.fromStep), ...bt], texts: [...s.scenario.steps.slice(0, b.fromStep), ...b.steps], wrong: b.grade === "error" || b.grade === "poor" };
}

function TrackBoard({ s, tr, level, onStep, autoplay, compact }: { s: CompiledScenario; tr: Track; level: Level; onStep?: (i: number) => void; autoplay?: boolean; compact?: boolean }) {
  const board = useRef<BoardHandle>(null);
  const pieces = useMemo(() => piecesFromScene(s.scenario.scene), [s.scenario.scene]);
  const pb = usePlayback(tr.steps, board, { autoplay, pauseAfter: (i) => Boolean(tr.texts[i]?.pause), onStep });
  const cur = tr.steps[pb.step];
  const pose = useMemo(() => (cur ? poseAt(cur, 0) : poseAt(tr.steps[0]!, 0)), [cur, tr.steps]);
  const child = activeChild();
  const quizOverlay = cur?.overlays.find((o) => o.type === "quizZone");
  const zone = useMemo(() => {
    if (!quizOverlay || quizOverlay.type !== "quizZone" || !pb.stepDone) return null;
    const f = frameOf(s, quizOverlay.quiz, "original");
    return f ? cellsOf(f, child?.stage ?? s.scenario.ageStages[0] ?? "A") : null;
  }, [quizOverlay, pb.stepDone, s, child?.stage]);
  const text = tr.texts[pb.step];
  useEffect(() => {
    // Озвучка шага в родительском режиме — по кнопке (CR-TTS-08); фразы заранее декодируются.
    audio.preload(tr.texts.map((x) => phraseUrl(s, x.kid)));
  }, [tr.texts, s]);
  return (
    <div className={`player__board ${tr.wrong ? "is-wrong" : ""}`}>
      {tr.wrong ? <div className="wrong-label">{t.player.wrongLabel}</div> : null}
      <div className="board-box">
        <Board ref={board} field={s.field} pieces={pieces} pose={pose} arrows={cur?.segments} overlays={pb.stepDone ? cur?.overlays : []} zone={zone} wrong={tr.wrong} ariaLabel={`${s.scenario.title}. ${text?.parent ?? ""}`} />
      </div>
      <div className="playbar" role="toolbar" aria-label="Воспроизведение">
        <button className="icon-btn" onClick={pb.prev} aria-label={t.player.prev} disabled={pb.step === 0}>
          ⏮
        </button>
        <button className="icon-btn" onClick={pb.replay} aria-label={t.player.replay}>
          ⟲
        </button>
        <button className="icon-btn icon-btn--primary" onClick={pb.playing ? pb.pause : pb.play} aria-label={pb.playing ? t.player.pause : t.player.play}>
          {pb.playing ? "❚❚" : "▶"}
        </button>
        <button className="icon-btn" onClick={pb.next} aria-label={t.player.next} disabled={pb.step >= tr.steps.length - 1}>
          ⏭
        </button>
        {!compact ? (
          <select aria-label={t.player.speed} value={pb.speed} onChange={(e) => pb.setSpeed(Number(e.target.value))}>
            <option value={0.5}>0,5×</option>
            <option value={1}>1×</option>
            <option value={1.5}>1,5×</option>
          </select>
        ) : null}
        <span className="playbar__step">{t.player.step(pb.step + 1, tr.steps.length)}</span>
      </div>
      {text ? (
        <div className="caption">
          <p className={level === "kid" ? "caption__kid" : ""}>{text[level]}</p>
          <button className="icon-btn" onClick={() => void audio.unlock().then(() => audio.play(phraseUrl(s, text.kid), text.kid))} aria-label={t.common.listen}>
            <SpeakerIcon />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Player() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const content = useApp((s) => s.content);
  const ref = content ? findScenario(content, id) : undefined;
  const [level, setLevel] = useState<Level>("parent");
  const [branch, setBranch] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);
  const [complaint, setComplaint] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const viewed = useRef(0);
  const s = ref?.scenario;
  const tr = useMemo(() => (s ? track(s, branch) : null), [s, branch]);
  const wrongBranch = s?.scenario.branches?.find((b) => b.grade === "error" || b.grade === "poor");

  useEffect(() => {
    if (!s || !tr) return;
    viewed.current = Math.max(viewed.current, step + 1);
    if (branch && step >= tr.steps.length - 1) {
      const child = activeChild();
      if (child) void record(child.id, "branch_viewed", { scenario_id: s.scenario.id, branch_id: branch });
    }
  }, [step, s, tr, branch]);
  useEffect(
    () => () => {
      const child = activeChild();
      if (child && s && viewed.current) {
        const total = s.timelines.main?.steps.length ?? 1;
        void record(child.id, "scenario_viewed", { scenario_id: s.scenario.id, content_version: s.version, percent: Math.round((Math.min(viewed.current, total) / total) * 100), level });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [s],
  );

  if (!content || !s || !tr) {
    return (
      <div className="page">
        <TopBar title="" back={() => nav(-1)} />
        <p>{t.player.notFound}</p>
      </div>
    );
  }
  const sc = s.scenario;
  const ex = sc.after.homeExercise ? findExercise(content, sc.after.homeExercise) : undefined;
  const firstQuiz = sc.quizzes?.[0];
  const p = s.passport;
  const outcome = branch ? sc.branches?.find((b) => b.id === branch)?.outcome : undefined;

  return (
    <div className="page player">
      <TopBar
        title={sc.title}
        back={() => nav(-1)}
        actions={
          <button className="link" onClick={() => setComplaint(t.player.report)}>
            {t.player.report}
          </button>
        }
      />
      <div className="player__layout">
        <div className="player__main">
          {wrongBranch ? (
            <div className="segmented" role="tablist" aria-label="Вариант">
              <button role="tab" aria-selected={!branch && !compare} className={!branch && !compare ? "is-active" : ""} onClick={() => (setBranch(null), setCompare(false))}>
                ✔ {t.player.correct}
              </button>
              <button role="tab" aria-selected={branch === wrongBranch.id && !compare} className={branch === wrongBranch.id && !compare ? "is-active is-wrong" : ""} onClick={() => (setBranch(wrongBranch.id), setCompare(false))}>
                ✗ {t.player.wrong}
              </button>
              <button role="tab" aria-selected={compare} className={`only-large ${compare ? "is-active" : ""}`} onClick={() => setCompare(true)}>
                ⇆ Сравнить
              </button>
            </div>
          ) : null}
          {compare && wrongBranch ? (
            <div className="compare">
              <TrackBoard s={s} tr={track(s, null)} level={level} autoplay compact />
              <TrackBoard s={s} tr={track(s, wrongBranch.id)} level={level} autoplay compact />
            </div>
          ) : (
            <TrackBoard key={branch ?? "main"} s={s} tr={tr} level={level} onStep={setStep} />
          )}
          {outcome && step >= tr.steps.length - 1 ? (
            <div className="callout callout--wrong">
              <strong>{t.player.outcome}:</strong> {outcome[level]}
              {tr.wrong ? (
                <>
                  {" "}
                  <button
                    className="link"
                    onClick={() => {
                      setBranch(null);
                      void audio.play(uiUrl(content, "try_again"), uiText(content, "try_again", t.quiz.tryAgain));
                    }}
                  >
                    {uiText(content, "try_again", t.quiz.tryAgain)}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <aside className="player__side">
          <div className="segmented" role="tablist" aria-label="Уровень объяснения">
            {(["kid", "parent", "coach"] as Level[]).map((l) => (
              <button key={l} role="tab" aria-selected={level === l} className={level === l ? "is-active" : ""} onClick={() => setLevel(l)}>
                {t.player.levels[l]}
              </button>
            ))}
          </div>
          {firstQuiz ? (
            <button className="btn btn--primary btn--big" onClick={() => nav(`/scenario/${encodeURIComponent(sc.id)}/quiz/${firstQuiz.id}`)}>
              {t.player.tryIt}
            </button>
          ) : null}
          <details open>
            <summary>{t.player.why}</summary>
            <p>{sc.explain.why}</p>
          </details>
          <details>
            <summary>{t.player.mistakes}</summary>
            <ul>{sc.explain.mistakes.map((m) => <li key={m}>{m}</li>)}</ul>
          </details>
          <details>
            <summary>{t.player.sayToKid}</summary>
            <ul>{sc.explain.sayToKid.map((m) => <li key={m}>«{m}»</li>)}</ul>
          </details>
          {ex ? (
            <details>
              <summary>{t.player.home}</summary>
              <Link to={`/exercise/${encodeURIComponent(ex.id)}`}>{ex.title}</Link>
            </details>
          ) : null}
          {sc.after.askAfterTraining ? (
            <details>
              <summary>{t.player.askAfter}</summary>
              <p>«{sc.after.askAfterTraining}»</p>
            </details>
          ) : null}
          <details>
            <summary>{t.player.howMade}</summary>
            <p>{p.devUnapproved ? t.player.howMadeDev : t.player.howMadeText}</p>
            {p.sources.length ? (
              <>
                <p className="muted">{t.player.sources}:</p>
                <ul>{p.sources.map((x) => <li key={x}>{x}</li>)}</ul>
              </>
            ) : null}
            <p className="muted small">
              {t.player.lastCheck}: {new Date(p.lastCheckedAt).toLocaleDateString("ru-RU")}
            </p>
          </details>
        </aside>
      </div>
      {complaint ? <ComplaintSheet draft={{ content_id: sc.id, content_version: s.version, step: tr.texts[step] ? String(tr.texts[step]!.n) : undefined }} onClose={() => setComplaint(null)} /> : null}
    </div>
  );
}
